import React from 'react';
import { Card, Typography, Row, Col } from 'antd';
import { Usage, PricingAnalytics } from '../../types';

const { Text, Title } = Typography;

type UsagePricingCardsProps = {
  usage: Usage;
  pricingAnalytics: PricingAnalytics | null;
  loading: boolean;
};

type MessageTypeData = {
  label: string;
  count: number;
  cost?: number;
  color?: string;
};

const UsagePricingCards = ({ usage, pricingAnalytics, loading }: UsagePricingCardsProps) => {
  // Use real data from API or fallback to legacy calculation
  const allMessages = pricingAnalytics?.allMessages || {
    messagesSent: usage.PAID_BUSINESS + usage.PAID_USER,
    messagesDelivered: usage.PAID_BUSINESS + usage.PAID_USER,
    messagesReceived: usage.PAID_USER,
  };

  const allDeliveries = pricingAnalytics?.allDeliveries || {
    total: usage.FREE_TIER + usage.FREE_ENTRY + usage.PAID_BUSINESS + usage.PAID_USER,
    marketing: Math.round(usage.PAID_BUSINESS * 0.3),
    utility: Math.round(usage.PAID_BUSINESS * 0.6),
    authentication: Math.round(usage.PAID_BUSINESS * 0.1),
    authenticationInternational: 0,
    service: usage.FREE_TIER + usage.FREE_ENTRY,
  };

  const freeDeliveries = pricingAnalytics?.freeDeliveries || {
    total: usage.FREE_TIER + usage.FREE_ENTRY,
    freeCustomerService: usage.FREE_TIER,
    freeEntryPoint: usage.FREE_ENTRY,
  };

  const paidDeliveries = pricingAnalytics?.paidDeliveries || {
    total: usage.PAID_BUSINESS + usage.PAID_USER,
    marketing: Math.round(usage.PAID_BUSINESS * 0.3),
    utility: Math.round(usage.PAID_BUSINESS * 0.6),
    authentication: Math.round(usage.PAID_BUSINESS * 0.1),
    authenticationInternational: 0,
  };

  const approximateCharges = pricingAnalytics?.approximateCharges || {
    total: usage.totalCost,
    marketing: Math.round(usage.PAID_BUSINESS * 0.3 * 0.075 * 100) / 100,
    utility: Math.round(usage.PAID_BUSINESS * 0.6 * 0.011 * 100) / 100,
    authentication: Math.round(usage.PAID_BUSINESS * 0.1 * 0.0095 * 100) / 100,
    authenticationInternational: 0,
  };

  const messageTypeIndicator = (color: string) => (
    <span
      style={{
        display: 'inline-block',
        width: '8px',
        height: '8px',
        backgroundColor: color,
        borderRadius: '50%',
        marginRight: '8px',
      }}
    />
  );

  const renderMessageTypeRow = (data: MessageTypeData) => (
    <div key={data.label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {data.color && messageTypeIndicator(data.color)}
        <Text style={{ fontSize: '13px' }}>{data.label}</Text>
      </div>
      <Text style={{ fontSize: '13px', fontWeight: 500 }}>{data.count.toLocaleString()}</Text>
    </div>
  );

  const renderCostRow = (data: MessageTypeData) => (
    <div key={data.label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {data.color && messageTypeIndicator(data.color)}
        <Text style={{ fontSize: '13px' }}>{data.label}</Text>
      </div>
      <Text style={{ fontSize: '13px', fontWeight: 500 }}>${data.cost?.toFixed(2)}</Text>
    </div>
  );

  return (
    <div className="usage-pricing-cards" style={{ padding: '16px 0' }}>
      <div className="note-banner">
        <Text style={{ fontSize: '13px', color: '#1890ff' }}>
          <strong>Note:</strong> All insights data is approximate and may differ from what shown on your invoices due to
          small variations in data processing.
        </Text>
      </div>

      <Row gutter={[24, 24]} style={{ marginTop: '8px' }}>
        {/* All messages card */}
        <Col xs={24} md={12} xl={8}>
          <Card
            loading={loading}
            title={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {messageTypeIndicator('#1890ff')}
                <span>All messages</span>
              </div>
            }
            style={{ height: '100%', backgroundColor: '#fff' }}
          >
            {renderMessageTypeRow({ label: 'Messages sent', count: allMessages.messagesSent, color: '#52c41a' })}
            {renderMessageTypeRow({
              label: 'Messages delivered',
              count: allMessages.messagesDelivered,
              color: '#1890ff',
            })}
            {/* {renderMessageTypeRow({
              label: 'Messages received',
              count: allMessages.messagesReceived,
              color: '#722ed1',
            })} */}
          </Card>
        </Col>

        {/* All deliveries card */}
        <Col xs={24} md={12} xl={8}>
          <Card
            loading={loading}
            title={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <div>
                  <span>{messageTypeIndicator('#52c41a')}</span>
                  <span>All deliveries</span>
                </div>
                <span>{allDeliveries.total.toLocaleString()}</span>
              </div>
            }
            style={{ height: '100%', backgroundColor: '#fff' }}
          >
            {renderMessageTypeRow({ label: 'Marketing', count: allDeliveries.marketing, color: '#fa8c16' })}
            {renderMessageTypeRow({ label: 'Utility', count: allDeliveries.utility, color: '#13c2c2' })}
            {renderMessageTypeRow({ label: 'Authentication', count: allDeliveries.authentication, color: '#eb2f96' })}
            {renderMessageTypeRow({
              label: 'Authentication - international',
              count: allDeliveries.authenticationInternational,
              color: '#f5222d',
            })}
            {renderMessageTypeRow({ label: 'Service', count: allDeliveries.service, color: '#52c41a' })}
          </Card>
        </Col>

        {/* Free deliveries card */}
        <Col xs={24} md={12} xl={8}>
          <Card
            loading={loading}
            title={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <div>
                  <span>{messageTypeIndicator('#52c41a')}</span>
                  <span>Free deliveries</span>
                </div>
                <span>{freeDeliveries.total.toLocaleString()}</span>
              </div>
            }
            style={{ height: '100%', backgroundColor: '#fff' }}
          >
            {renderMessageTypeRow({
              label: 'Free customer service',
              count: freeDeliveries.freeCustomerService,
              color: '#52c41a',
            })}
            {renderMessageTypeRow({
              label: 'Free entry point',
              count: freeDeliveries.freeEntryPoint,
              color: '#722ed1',
            })}
          </Card>
        </Col>

        {/* Paid deliveries card */}
        <Col xs={24} md={12} xl={8}>
          <Card
            loading={loading}
            title={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <div>
                  <span>{messageTypeIndicator('#fa8c16')}</span>
                  <span>Paid deliveries</span>
                </div>
                <span>{paidDeliveries.total.toLocaleString()}</span>
              </div>
            }
            style={{ height: '100%', backgroundColor: '#fff' }}
          >
            {renderMessageTypeRow({ label: 'Marketing', count: paidDeliveries.marketing, color: '#fa8c16' })}
            {renderMessageTypeRow({ label: 'Utility', count: paidDeliveries.utility, color: '#13c2c2' })}
            {renderMessageTypeRow({ label: 'Authentication', count: paidDeliveries.authentication, color: '#eb2f96' })}
            {renderMessageTypeRow({
              label: 'Authentication - international',
              count: paidDeliveries.authenticationInternational,
              color: '#f5222d',
            })}
          </Card>
        </Col>

        {/* Approximate charges card */}
        <Col xs={24} md={12} xl={8}>
          <Card
            loading={loading}
            title={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <div>
                  <span>{messageTypeIndicator('#1890ff')}</span>
                  <span>Approximate charges</span>
                </div>
                <span>${approximateCharges.total.toFixed(2)}</span>
              </div>
            }
            style={{ height: '100%', backgroundColor: '#fff' }}
          >
            {renderCostRow({ label: 'Marketing', cost: approximateCharges.marketing, color: '#fa8c16' })}
            {renderCostRow({ label: 'Utility', cost: approximateCharges.utility, color: '#13c2c2' })}
            {renderCostRow({ label: 'Authentication', cost: approximateCharges.authentication, color: '#eb2f96' })}
            {renderCostRow({
              label: 'Authentication - international',
              cost: approximateCharges.authenticationInternational,
              color: '#f5222d',
            })}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default UsagePricingCards;
